// Rewrites the dead demo links Blake's bundle ships (owner instruction,
// 2026-08-23: "point all the footer and buttons correctly and the sign-in
// button, do it all"). His entire link graph is internal-only — every href
// in app.js/data.js is a `#/...` demo hash, and the sign-in and store-badge
// controls are plain <button>s wired to a toast or to #/auth (which
// blockAuthRoute in floorTransport.ts now redirects to home, making
// #btn-signin a dead control). His files are read-only, so every fix here
// lives in this module and is applied to the DOM after the fact.
//
// WHY A MutationObserver AND NOT A WRAPPED render() (the blockAuthRoute
// pattern one file over): route() is only ever called from INSIDE render()
// via a fresh unqualified lookup, so reassigning window.route (a classic
// script's top-level `function` IS a window property — see floorTransport.ts)
// is seen by every call site. render() is different: app.js:5300 does
// `window.addEventListener("hashchange", render)`, which evaluates `render`
// ONCE at that line and hands the browser's event system the resulting
// function OBJECT — not a live binding. That listener is permanently bound to
// the ORIGINAL render, and reassigning window.render afterward does not
// change what it invokes. Since most of Blake's own navigation
// (data-godiscover, data-gocat, #btn-signin's own #/auth jump, ...) sets
// location.hash and relies on exactly that listener — never calling render()
// directly — wrapping render() the way blockAuthRoute wraps route() would
// silently miss the majority of re-renders. A MutationObserver on #app
// doesn't care which internal path produced the DOM change; it fires
// whenever `app.innerHTML = ...` actually runs, which is the one thing every
// one of Blake's 165 rerenderKeepScroll/render() call sites has in common.
//
// SELF-TERMINATION: applyLinkFixups mutates #app's subtree (attribute writes,
// and element replacement for the buttons and the one delabeled Trust link),
// which the observer is watching. Every fixup checks the CURRENT state before
// changing anything, so the observer's own re-entrant invocation (triggered
// by the fixups' own edits) finds nothing left to do and produces no further
// mutations — one real Blake render costs exactly two passes, not a loop.
//
// STRUCTURAL MATCHING ONLY (never index position, per the task brief): the
// sign-in button by its stable id, the store badges by data-store inside
// .store-badges, and footer links by their column's .h.microlabel text plus
// their own visible text — the same properties a human would use to find
// them if Blake reshuffles his markup in a future edit pass. A match that
// isn't found warns loudly (console.warn) instead of silently leaving a
// legal link pointed at the wrong page.

/** Real, verified destinations (curl + a live render check against
 *  https://gobazaar-staging.replit.app on 2026-08-23 — see the task's own
 *  report for the full table). Relative paths only: these render inside the
 *  same SPA shell that already serves /web, so a relative href resolves
 *  against gobazaar.co in prod and the staging host on staging, exactly like
 *  appLinks.ts's getAppRedirect(). */
const IOS_HREF = "/get/ios?src=website&placement=nav";
const ANDROID_HREF = "/get/android?src=website&placement=nav";

/**
 * Market column — WE NO LONGER TOUCH IT, and the removal is the fix.
 *
 * This column used to be rewritten: "Browse listings" → /browse and "Your
 * collection" → /profile. Both were correct WHEN WRITTEN and are wrong now,
 * because the reason has expired.
 *
 * 🔑 THE EXPIRED PREMISE. The original note here read: "#/deals is gated behind
 * store.authed, which floorTransport.ts's bridge pins permanently false." That
 * was true — every marketplace view behind that flag was unreachable, so
 * sending a visitor to a support-site page was the only honest destination
 * available. floorTransport.ts:199 now reads "✅ store.authed NOW TRACKS
 * IDENTITY. THE PIN IS OFF." The flag follows the real session, the authed
 * views render, and the redirect outlived its own justification.
 *
 * 🔴 AND BLAKE'S OWN HREFS WERE ALREADY RIGHT. app.js:697 ships:
 *     <a href="#/discover">Discover</a>
 *     <a href="#/">Browse listings</a>
 *     <a href="#/collections">Top collections</a>
 *     <a href="#/deals">Your deals</a>
 *     <a href="#/profile">Your collection</a>
 * Five links, all pointing at working marketplace views. Overriding two of them
 * sent a visitor OUT of the marketplace to a parallel page — reported by the
 * owner 2026-08-28: signing in and opening "Your collection" landed on a
 * different profile from the one his own avatar opens. Deleting the override
 * restores his, rather than repointing ours somewhere new.
 *
 * 🪤 /browse AND /profile ARE NOT DEAD, and this is not a claim that they are.
 * Both return 200; /browse server-renders 41 real items with its own SEO
 * templates (api-server/src/app.ts:287, seo/templates/browsePage.ts). They stay
 * exactly where they are, for crawlers and pasted links. They are simply not
 * where an in-app footer click belongs.
 *
 * THE RULE, for the next person deciding this: leave the marketplace shell only
 * when the bundle has no working surface for the intent. Legal, help and real
 * auth qualify — the Trust and Company columns below are still rewritten for
 * exactly that reason, and #wall-signin still goes to the real /login because
 * the bundle's own #/auth is a demo flow with no authentication behind it.
 */

/** Trust column. "Price checks" has no entry — Blake's l.priceCheck is a UI
 *  concept with no live help article and no backing data wired in this pass
 *  (see floorTransport.ts's gap log); DELABEL_TEXT below turns it into a
 *  plain label instead of a link to the wrong page. */
const TRUST_LINKS: Readonly<Record<string, string>> = {
  "Escrow on every deal": "/help/escrow",
  // /help/reputation's "Community Price Voting" section (vote whether a
  // price is fair/steal/too-high) is the real feature "Community verdicts"
  // describes — closer than any other guide on the site.
  "Community verdicts": "/help/reputation",
};

/** Footer links with no honest destination — converted to a non-interactive
 *  label rather than left pointing at "#/" (the same placeholder every OTHER
 *  unwired demo link uses, so leaving it as-is would look wired but isn't). */
const DELABEL_TEXT: ReadonlySet<string> = new Set(["Price checks"]);

/** Company column's four existing links. */
const COMPANY_LINKS: Readonly<Record<string, string>> = {
  About: "/about",
  Terms: "/terms",
  Privacy: "/privacy",
  // /contact is a ticket-submission form; /help is the self-serve FAQ/guide
  // hub with its own search box — closer to what a footer's generic
  // "Support" link means on most sites, and /contact is offered separately
  // (as "Submit a Ticket") from inside /help itself.
  Support: "/help",
};

/** The four legal/company links the footer is missing entirely. All four
 *  verified live (200, correct title and body) on 2026-08-23 — including
 *  /accessibility, which the task brief's own "real routes" list omitted but
 *  which App.tsx registers and which resolves correctly; verifying instead of
 *  trusting that list is what turned it up. */
const COMPANY_EXTRA_LINKS: ReadonlyArray<{ text: string; href: string }> = [
  { text: "DMCA", href: "/dmca" },
  { text: "Refund Policy", href: "/refund-policy" },
  { text: "Contact", href: "/contact" },
  { text: "Accessibility", href: "/accessibility" },
];




/* 🔴 RETIRED 2026-08-31 (Blake's auth pop-up restore): fixSigninButton,
   fixWallSignin, and addCreateAccount are all gone from this module.
   The first two rewrote the dead sign-in <button>s into <a href="/login">;
   the third injected a "Create an account" <a> beside each of them (plus the
   marker class that scoped R27's mobile banner in overrides.css). All three
   solved "the demo's auth buttons go nowhere on live" — and all three now
   FIGHT the in-page auth modal those buttons open (app.js openAuthModal:
   sign-in / register views over whatever page the visitor is on, wired to the
   real endpoints through the shell's __gbzAuth adapter). The wall offers both
   doors itself, Start Trading opens register directly, and /register + /login
   redirect into the marketplace's own day-mode auth. Nothing here to fix. */

function fixStoreBadge(doc: Document, container: Element, store: "ios" | "android", href: string): void {
  const el = container.querySelector(`[data-store="${store}"]`);
  if (!el) {
    console.warn(`[web-install] link fixup: no [data-store="${store}"] element found inside .store-badges`);
    return;
  }
  if (el.tagName === "A") return; // already fixed this pass
  const a = doc.createElement("a");
  a.className = el.className;
  a.setAttribute("data-store", store);
  a.setAttribute("href", href);
  a.innerHTML = el.innerHTML;
  el.replaceWith(a);
}

function fixStoreBadges(doc: Document): void {
  // Absent entirely when store.appctas is off (Blake's own toggle, suppressed
  // demo bar aside) — a legitimate feature state, not a structural mismatch,
  // so this does not warn on its own.
  const container = doc.querySelector(".store-badges");
  if (!container) return;
  fixStoreBadge(doc, container, "ios", IOS_HREF);
  fixStoreBadge(doc, container, "android", ANDROID_HREF);
}

/** Converts one footer <a> into a plain, unstyled-as-a-link <span> carrying
 *  the same text — "a label that is not a link is more honest than a link to
 *  the wrong page" (task brief). Marked with data-gbz-label so a later pass
 *  (the observer's own self-retrigger) recognizes it as already fixed. */
function delabelLink(doc: Document, el: Element): void {
  if (el.tagName === "SPAN" && el.hasAttribute("data-gbz-label")) return;
  const span = doc.createElement("span");
  span.setAttribute("data-gbz-label", "1");
  // Mirrors .foot-col a's own resting style (styles.css) minus the hover
  // brightening and pointer cursor a real link implies.
  span.style.cssText = "display:block;color:var(--ink-muted);padding:5px 0;font-size:14.5px;cursor:default;";
  span.textContent = el.textContent ?? "";
  el.replaceWith(span);
}

function fixColumnLinks(
  doc: Document,
  col: Element,
  linkMap: Readonly<Record<string, string>>,
  delabels: ReadonlySet<string> = new Set(),
): void {
  const seen = new Set<string>();
  col.querySelectorAll("a").forEach((a) => {
    const text = a.textContent?.trim() ?? "";
    if (text in linkMap) {
      seen.add(text);
      const target = linkMap[text];
      if (a.getAttribute("href") !== target) a.setAttribute("href", target);
    } else if (delabels.has(text)) {
      seen.add(text);
      delabelLink(doc, a);
    }
  });
  // A delabelled entry is no longer an <a>, so the loop above can never see it
  // again. Count the spans a previous pass produced as found — otherwise the
  // fixup warns that its OWN completed work is missing, every time the
  // observer re-runs over an already-fixed footer.
  col.querySelectorAll("span[data-gbz-label]").forEach((span) => {
    const text = span.textContent?.trim() ?? "";
    if (delabels.has(text)) seen.add(text);
  });
  for (const key of [...Object.keys(linkMap), ...delabels]) {
    if (!seen.has(key)) {
      console.warn(`[web-install] link fixup: expected footer link "${key}" not found in this column`);
    }
  }
}

function addCompanyExtras(doc: Document, col: Element): void {
  const existingTexts = new Set(Array.from(col.querySelectorAll("a")).map((a) => a.textContent?.trim() ?? ""));
  for (const { text, href } of COMPANY_EXTRA_LINKS) {
    if (existingTexts.has(text)) continue; // already injected this pass
    const a = doc.createElement("a");
    a.setAttribute("href", href);
    a.textContent = text;
    col.appendChild(a);
  }
}

function fixFooter(doc: Document): void {
  const cols = doc.querySelectorAll(".foot-col");
  if (cols.length === 0) return; // no footer on this view (e.g. the auth view — blocked anyway)

  let sawMarket = false;
  let sawTrust = false;
  let sawCompany = false;

  cols.forEach((col) => {
    const label = col.querySelector(".h.microlabel")?.textContent?.trim();
    if (label === "Market") {
      // Deliberately untouched — see the Market column note above. Still
      // DETECTED, because the structural warning below is what tells us his
      // footer moved under us, and that is worth knowing whether or not we
      // rewrite anything in it.
      sawMarket = true;
    } else if (label === "Trust") {
      sawTrust = true;
      fixColumnLinks(doc, col, TRUST_LINKS, DELABEL_TEXT);
    } else if (label === "Company") {
      sawCompany = true;
      fixColumnLinks(doc, col, COMPANY_LINKS);
      addCompanyExtras(doc, col);
    }
  });

  if (!sawMarket) console.warn('[web-install] link fixup: no footer column labeled "Market" found');
  if (!sawTrust) console.warn('[web-install] link fixup: no footer column labeled "Trust" found');
  if (!sawCompany) console.warn('[web-install] link fixup: no footer column labeled "Company" found');
}

/** Applied once immediately (for the EOF render() app.js already fired
 *  before this module gets a chance to run) and then on every subsequent DOM
 *  change inside #app for the life of the tab. */
export function applyLinkFixups(doc: Document): void {
  /* fixSigninButton / fixWallSignin / addCreateAccount: retired — see the
     note above. The auth buttons are live controls now (they open the modal),
     so any rewrite or sibling injection here would defeat them. */
  fixStoreBadges(doc);
  fixFooter(doc);
}

/** Must run after app.js has loaded (the elements this rewrites don't exist
 *  before its EOF render()) — see WebInstall.tsx's call site, same ordering
 *  requirement as blockAuthRoute. */
export function installLinkFixups(win: Window): void {
  const doc = win.document;
  const app = doc.getElementById("app");
  if (!app) {
    console.warn("[web-install] installLinkFixups: #app not found, link fixups not installed");
    return;
  }
  applyLinkFixups(doc);
  const observer = new MutationObserver(() => {
    applyLinkFixups(doc);
  });
  observer.observe(app, { childList: true, subtree: true });
}
